import { supabase } from "@/lib/supabase";
import { readWorkspaceOrgIdFromStorage } from "@/lib/workspaceOrgStorage";
import type { InboundReceiptDetail, InboundReceiptRow, ReceiptSourceOption } from "@/lib/ops/inboundReceiptsModel";

export type { InboundReceiptDetail, InboundReceiptRow, ReceiptSourceOption };

export type InboundReceiptsListQuery = {
  organization_id: string;
  source?: string;
  processing_status?: string;
  unlinked_only?: boolean;
  since?: string;
  until?: string;
  limit?: number;
};

type FetchResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string };

const ACTION = "/api/admin/action?action=inbound-receipts";

async function postInboundReceipts<T>(body: Record<string, unknown>): Promise<FetchResult<T>> {
  if (!supabase) return { ok: false, status: 503, error: "App is not configured for sign-in." };
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, status: 401, error: "Sign in required." };

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const ws = readWorkspaceOrgIdFromStorage();
  if (ws) headers["X-Workspace-Organization-Id"] = ws;

  const r = await fetch(ACTION, { method: "POST", headers, body: JSON.stringify(body) });
  const json = (await r.json().catch(() => ({}))) as { ok?: boolean; error?: string } & T;
  if (!r.ok || json.ok === false) {
    return { ok: false, status: r.status, error: json.error || `HTTP ${r.status}` };
  }
  return { ok: true, status: r.status, data: json };
}

export async function listInboundReceipts(query: InboundReceiptsListQuery) {
  return postInboundReceipts<{ ok: true; events: InboundReceiptRow[]; sources: ReceiptSourceOption[]; limit: number }>({
    op: "list",
    ...query,
  });
}

export async function getInboundReceipt(organizationId: string, id: string) {
  return postInboundReceipts<{ ok: true; event: InboundReceiptDetail }>({
    op: "get",
    organization_id: organizationId,
    id,
  });
}
